import {
  type BilateralTradeCsvRowResult,
  BilateralTradeCsvRowStatus,
} from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Text } from '@enechain/ecloud-designsystem'

import {
  CsvDiffErrorText,
  CsvDiffTable,
  type CsvDiffTableColumn,
  type CsvDiffTone,
  csvDiffBadgeSpec,
} from '~/components/common/CsvUpload/CsvDiffTable'
import {
  type EnumBadgeSpec,
  EnumStatusBadge,
} from '~/components/common/EnumStatusBadge'
import type { BilateralTradeCsvRow } from '~/models/bilateralTradeCsv'
import type { PreviewFilter } from '~/pages/BilateralTradeCsvUpload/components/FilterCards'

type Props = {
  /** チェック RPC が返した行ごとの差分プレビュー結果 (backend 計算値)。 */
  rows: BilateralTradeCsvRowResult[]
  /**
   * client parse 結果 (rowNumber キー引き)。BilateralTradeCsvRowResult
   * (response) は row_number / status / public_trade_id / message のみを
   * 持つため、表示列 (取引先 / 売買 / 受渡期間 / 数量 (MW)) は
   * 送信元の CSV セルから取り出す (= サーバー計算値の再計算ではなくユーザー
   * 入力の再表示で、業務判断には該当しない)。
   */
  parsedRows: BilateralTradeCsvRow[]
  /** Step2 のクリック式フィルタ選択値。フィルタリングは表示用の絞り込みであり、件数の再計算ではない。 */
  filter: PreviewFilter
}

/** プレビュー表の 1 行 (backend のステータス + CSV セル由来の表示列)。 */
type PreviewRow = {
  rowNumber: number
  status: BilateralTradeCsvRowStatus
  publicTradeId: string
  partner: string
  buySell: string
  deliveryPeriod: string
  volumeMw: string
  message: string
}

/**
 * 行ステータス → 差分表の行の種類 (行の地色・バッジの色を決める)。
 * `Record` の網羅性が proto enum の値追加への追従を型で強制する。
 */
const ROW_STATUS_TONE: Record<
  BilateralTradeCsvRowStatus,
  CsvDiffTone | undefined
> = {
  [BilateralTradeCsvRowStatus.UNSPECIFIED]: undefined,
  [BilateralTradeCsvRowStatus.UNCHANGED]: 'unchanged',
  [BilateralTradeCsvRowStatus.CREATED]: 'new',
  [BilateralTradeCsvRowStatus.UPDATED]: 'changed',
  [BilateralTradeCsvRowStatus.DELETED]: 'deleted',
  [BilateralTradeCsvRowStatus.ERROR]: 'error',
}

/**
 * 行ステータス → 状態バッジ表示仕様。 色は `ROW_STATUS_TONE` と同じ写像から引く
 * (design: 新規 = 緑 / 変更 = 黄 / 変更なし = 灰 / 削除・エラー = 赤)。
 */
const ROW_STATUS_BADGE: Record<BilateralTradeCsvRowStatus, EnumBadgeSpec> = {
  [BilateralTradeCsvRowStatus.UNSPECIFIED]: {
    status: 'neutral',
    labelKey: 'page:bilateralTradeCsvUpload.rowStatus.unspecified',
  },
  [BilateralTradeCsvRowStatus.UNCHANGED]: csvDiffBadgeSpec(
    'unchanged',
    'page:bilateralTradeCsvUpload.rowStatus.unchanged',
  ),
  [BilateralTradeCsvRowStatus.CREATED]: csvDiffBadgeSpec(
    'new',
    'page:bilateralTradeCsvUpload.rowStatus.created',
  ),
  [BilateralTradeCsvRowStatus.UPDATED]: csvDiffBadgeSpec(
    'changed',
    'page:bilateralTradeCsvUpload.rowStatus.updated',
  ),
  [BilateralTradeCsvRowStatus.DELETED]: csvDiffBadgeSpec(
    'deleted',
    'page:bilateralTradeCsvUpload.rowStatus.deleted',
  ),
  [BilateralTradeCsvRowStatus.ERROR]: csvDiffBadgeSpec(
    'error',
    'page:bilateralTradeCsvUpload.rowStatus.error',
  ),
}

/**
 * 売買の文字色 (design `.bt-bs-buy` = 買い色 / `.bt-bs-sell` = 売り色、 semibold)。
 * 値は CSV セルの文字列 (日本語の `買` / `売`、 英語の CSV の `Buy` / `Sell`) のままで、
 * それ以外 (空・不正値のエラー行) は色を付けない。
 */
const BUY_SELL_COLOR: Record<string, string> = {
  買: 'Text.bid',
  売: 'Text.offer',
  Buy: 'Text.bid',
  Sell: 'Text.offer',
}

/**
 * 差分プレビュー表の列定義。コンポーネント本体の外に置くことで、cell が
 * 「render のたびに新しいコンポーネント型」にならないようにする
 * (react/no-unstable-nested-components)。
 */
function buildPreviewColumns(
  t: TFunction,
): Array<CsvDiffTableColumn<PreviewRow>> {
  return [
    // ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断)。
    // 旧実装は 2 番目 (行番号 の後ろ) だったが、
    // design (`design/admin/uploads/bilateral-trade.html`) は既に先頭に置いており、
    // 本変更で design 準拠に揃う。
    {
      key: 'status',
      header: t('page:bilateralTradeCsvUpload.step2.columns.status'),
      cell: (row) => (
        <EnumStatusBadge map={ROW_STATUS_BADGE} value={row.status} />
      ),
    },
    {
      key: 'rowNumber',
      header: t('page:bilateralTradeCsvUpload.step2.columns.rowNumber'),
      align: 'right',
      cell: (row) => row.rowNumber,
    },
    {
      key: 'publicTradeId',
      header: t('page:bilateralTradeCsvUpload.step2.columns.publicTradeId'),
      mono: true,
      cell: (row) => row.publicTradeId,
    },
    {
      key: 'partner',
      header: t('page:bilateralTradeCsvUpload.step2.columns.partner'),
      cell: (row) => row.partner,
    },
    {
      key: 'buySell',
      header: t('page:bilateralTradeCsvUpload.step2.columns.buySell'),
      align: 'center',
      cell: (row) => {
        const color = BUY_SELL_COLOR[row.buySell]
        return color === undefined ? (
          row.buySell
        ) : (
          <Text as="span" color={color} fontWeight="semibold">
            {row.buySell}
          </Text>
        )
      },
    },
    {
      key: 'deliveryPeriod',
      header: t('page:bilateralTradeCsvUpload.step2.columns.deliveryPeriod'),
      cell: (row) => row.deliveryPeriod,
    },
    {
      key: 'volumeMw',
      header: t('page:bilateralTradeCsvUpload.step2.columns.volumeMw'),
      align: 'right',
      cell: (row) => row.volumeMw,
    },
    {
      key: 'message',
      header: t('page:bilateralTradeCsvUpload.step2.columns.message'),
      // エラー内容は備考列に出す (common.md 行レベルバリデーション・エラー仕様)
      cell: (row) =>
        row.status === BilateralTradeCsvRowStatus.ERROR ? (
          <CsvDiffErrorText>{row.message}</CsvDiffErrorText>
        ) : (
          row.message
        ),
    },
  ]
}

/** Step2 の行ステータスからクリック式フィルタを適用する (表示絞り込みのみ。値の再計算はしない)。 */
function filterPreviewRows(
  rows: PreviewRow[],
  filter: PreviewFilter,
): PreviewRow[] {
  switch (filter) {
    case 'all':
      return rows
    case 'diff':
      return rows.filter(
        (r) =>
          r.status !== BilateralTradeCsvRowStatus.UNCHANGED &&
          r.status !== BilateralTradeCsvRowStatus.ERROR,
      )
    case 'unchanged':
      return rows.filter(
        (r) => r.status === BilateralTradeCsvRowStatus.UNCHANGED,
      )
    case 'new':
      return rows.filter((r) => r.status === BilateralTradeCsvRowStatus.CREATED)
    case 'updated':
      return rows.filter((r) => r.status === BilateralTradeCsvRowStatus.UPDATED)
    case 'deleted':
      return rows.filter((r) => r.status === BilateralTradeCsvRowStatus.DELETED)
    case 'error':
      return rows.filter((r) => r.status === BilateralTradeCsvRowStatus.ERROR)
  }
}

/** Step2 差分プレビュー表 (backend のステータス + CSV セル由来の表示列)。 */
export const PreviewTable = ({
  rows,
  parsedRows,
  filter,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const columns = buildPreviewColumns(t)

  const cellsByRowNumber = new Map<number, BilateralTradeCsvRow['cells']>(
    parsedRows.map((r) => [r.rowNumber, r.cells]),
  )
  const previewRows: PreviewRow[] = rows.map((r) => {
    const cells = cellsByRowNumber.get(r.rowNumber)
    return {
      rowNumber: r.rowNumber,
      status: r.status,
      publicTradeId: r.publicTradeId,
      partner: cells?.['取引先'] ?? '',
      buySell: cells?.['売買'] ?? '',
      deliveryPeriod:
        cells === undefined ? '' : `${cells['開始日']}〜${cells['終了日']}`,
      volumeMw: cells?.['数量 (MW)'] ?? '',
      message: r.message,
    }
  })
  const filteredPreviewRows = filterPreviewRows(previewRows, filter)

  return (
    <CsvDiffTable
      columns={columns}
      getRowKey={(row) => String(row.rowNumber)}
      getRowTone={(row) => ROW_STATUS_TONE[row.status]}
      rows={filteredPreviewRows}
    />
  )
}
